import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn } from 'react-hook-form';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TextField from '../../../core/components/Fields/TextField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import Switch from '../../../core/components/Switch.tsx';
import { OUTLOOK_CATEGORY_COLORS, CategoryFormData } from './CreateCustomCategoryModal.tsx';
import { ForwardOption } from './types.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faBolt, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import Tooltip from '../../../core/components/Tooltip.tsx';
import AgentBadge from './AgentBadge.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const darkenHex = (hex: string, amount = 60): string => {
    const num = parseInt(hex.slice(1), 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0xff) - amount);
    const b = Math.max(0, (num & 0xff) - amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const BoltIcon = withIcon(faBolt);
const InfoIcon = withIcon(faInfoCircle);

const CHANNEL_TYPE_LABELS: Record<string, string> = {
    rfqProcessingChannels: 'Price Request',
    orderProcessingChannels: 'Order',
    orderConfirmationProcessingChannels: 'Order Confirmation',
    invoiceProcessingChannels: 'Invoice',
    propertyBillProcessingChannels: 'Property Bill',
    deliveryNoteProcessingChannels: 'Delivery Note',
    listOfServicesProcessingChannels: 'List of Services',
    universalProcessingChannels: 'Universal',
};

type DetectionFieldsProps = {
    form: UseFormReturn<CategoryFormData>;
    isLoading?: boolean;
    sourceType?: string;
    showHeader?: boolean;
    showKeywords?: boolean;
};

export const CategoryDetectionFields = ({
    form,
    isLoading = false,
    sourceType,
    showHeader = true,
    showKeywords = true,
}: DetectionFieldsProps) => {
    const isAgentCategory = sourceType === 'AGENT';
    const { t } = useTranslation('integrations');
    const formErrors = form.formState.errors;

    return (
        <>
            {showHeader && (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-content-strong">
                            {t('smartInbox.config.events.customCategories.form.aiInstructions')}
                        </span>
                        {isAgentCategory && <AgentBadge />}
                    </div>
                    <span className="text-sm font-normal text-gray-600">
                        {t(
                            isAgentCategory
                                ? 'smartInbox.config.events.customCategories.form.aiInstructionsDescriptionAgent'
                                : 'smartInbox.config.events.customCategories.form.aiInstructionsDescription'
                        )}
                    </span>
                </div>
            )}

            <Controller
                name="name"
                control={form.control}
                rules={{ required: t('smartInbox.config.events.customCategories.form.validation.nameRequired') }}
                render={({ field }) => (
                    <FormField
                        label={t('smartInbox.config.events.customCategories.form.name')}
                        error={formErrors.name}
                        required
                    >
                        <StringField {...field} disabled={isLoading} />
                    </FormField>
                )}
            />

            {!isAgentCategory && (
                <Controller
                    name="detectionCriteria"
                    control={form.control}
                    rules={{
                        required: t(
                            'smartInbox.config.events.customCategories.form.validation.detectionCriteriaRequired'
                        ),
                    }}
                    render={({ field }) => (
                        <FormField
                            label={t('smartInbox.config.events.customCategories.form.detectionCriteria')}
                            error={formErrors.detectionCriteria}
                            required
                        >
                            <TextField
                                {...field}
                                disabled={isLoading}
                                placeholder={t(
                                    'smartInbox.config.events.customCategories.form.detectionCriteriaPlaceholder'
                                )}
                            />
                        </FormField>
                    )}
                />
            )}

            {!isAgentCategory && showKeywords && (
                <Controller
                    name="supportingKeywords"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('smartInbox.config.events.customCategories.form.supportingKeywords')}
                            error={formErrors.supportingKeywords}
                        >
                            <TagsField
                                value={field.value || []}
                                onValueChange={field.onChange}
                                onBlur={field.onBlur}
                                placeholder={t(
                                    'smartInbox.config.events.customCategories.form.supportingKeywordsPlaceholder'
                                )}
                            />
                        </FormField>
                    )}
                />
            )}
        </>
    );
};

type OutlookActionFieldsProps = {
    form: UseFormReturn<CategoryFormData>;
    destinationFolders: Array<{ id: string; name: string }>;
    showHeader?: boolean;
};

export const CategoryOutlookActionFields = ({
    form,
    destinationFolders,
    showHeader = true,
}: OutlookActionFieldsProps) => {
    const { t } = useTranslation('integrations');
    const outlookTagValue = form.watch('outlookTag');
    const formErrors = form.formState.errors;
    const hasSavedTag = ((form.formState.defaultValues?.outlookTag as string[] | undefined)?.length ?? 0) > 0;

    return (
        <>
            {showHeader && (
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-content-strong">
                        {t('smartInbox.config.events.customCategories.form.outlookActions')}
                    </span>
                    <span className="text-sm font-normal text-gray-600">
                        {t('smartInbox.config.events.customCategories.form.outlookActionsDescription')}
                    </span>
                </div>
            )}

            <Controller
                name="outlookTag"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={
                            hasSavedTag
                                ? t('smartInbox.config.events.customCategories.form.outlookTag')
                                : t('smartInbox.config.events.customCategories.form.addOutlookTag')
                        }
                        error={formErrors.outlookTag}
                    >
                        <TagsField
                            disabled={hasSavedTag}
                            readOnly={hasSavedTag}
                            value={field.value || []}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            maxTags={1}
                            placeholder={t('smartInbox.config.events.customCategories.form.addOutlookTagPlaceholder')}
                        />
                    </FormField>
                )}
            />

            {outlookTagValue?.length > 0 && (
                <Controller
                    name="tagColor"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={
                                hasSavedTag
                                    ? t('smartInbox.config.events.customCategories.form.tagColor')
                                    : t('smartInbox.config.events.customCategories.form.pickTagColor')
                            }
                            error={formErrors.tagColor}
                        >
                            <div className="flex flex-wrap gap-2 pt-1">
                                {OUTLOOK_CATEGORY_COLORS.map((color) => (
                                    <button
                                        key={color.name}
                                        type="button"
                                        title={color.label}
                                        disabled={hasSavedTag}
                                        onClick={hasSavedTag ? undefined : () => field.onChange(color.name)}
                                        style={{
                                            backgroundColor: color.hex,
                                            ...(field.value === color.name
                                                ? {
                                                      boxShadow: `0 0 0 2px white, 0 0 0 4px ${darkenHex(color.hex)}`,
                                                  }
                                                : {}),
                                        }}
                                        className={classnames(
                                            'w-6 h-6 rounded-full transition-transform focus:outline-none',
                                            hasSavedTag ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'
                                        )}
                                    />
                                ))}
                            </div>
                        </FormField>
                    )}
                />
            )}

            <Controller
                name="moveToOutlookFolder"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('smartInbox.config.events.customCategories.form.moveToOutlookFolder')}
                        error={formErrors.moveToOutlookFolder}
                    >
                        <div onWheel={(e) => e.stopPropagation()}>
                            <SelectField
                                value={field.value || undefined}
                                options={destinationFolders.map((folder) => ({
                                    value: folder.id,
                                    label: folder.name,
                                }))}
                                renderOption={(option) => option.label}
                                placeholder={t(
                                    'smartInbox.config.events.customCategories.form.moveToOutlookFolderPlaceholder'
                                )}
                                onValueChange={field.onChange}
                            />
                        </div>
                    </FormField>
                )}
            />

            <Controller
                name="markAsRead"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-row gap-2 justify-between">
                        <span className="text-sm font-normal text-content-default">
                            {t('smartInbox.config.events.customCategories.form.markAsRead')}
                        </span>
                        <Switch value={field.value} onValueChange={field.onChange} />
                    </div>
                )}
            />
        </>
    );
};

type AutomationFieldsProps = {
    form: UseFormReturn<CategoryFormData>;
    channels: Record<string, any>;
};

export const CategoryAutomationFields = ({ form, channels }: AutomationFieldsProps) => {
    const { t } = useTranslation('integrations');
    const enableAutoforwarding = form.watch('enableAutoforwarding');
    const forwardOption = form.watch('forwardOption');
    const formErrors = form.formState.errors;

    const channelOptions = channels
        ? Object.entries(channels)
              .filter(([key]) => key !== 'agents' && key !== '__typename')
              .flatMap(([key, val]) =>
                  Array.isArray(val)
                      ? val
                            .filter((c: any) => c?.id && c?.name)
                            .map((c: any) => ({
                                value: c.id,
                                label: `${CHANNEL_TYPE_LABELS[key] ?? key}, ${c.name}`,
                            }))
                      : []
              )
        : [];

    return (
        <div className="flex flex-col gap-3 bg-surface-medium rounded p-4">
            <span className="flex flex-row gap-2 items-center font-semibold text-sm text-content-strong">
                <BoltIcon /> {t('smartInbox.config.events.customCategories.automation')}
            </span>
            <span className="text-sm font-normal text-content-default">
                {t('smartInbox.config.events.customCategories.automationDescription')}
            </span>
            <Controller
                name="enableAutoforwarding"
                control={form.control}
                render={({ field }) => (
                    <div className="flex flex-row gap-2 justify-between">
                        <span className="text-sm font-normal text-content-default mb-3">
                            {t('smartInbox.config.events.customCategories.form.enableForwarding')}
                        </span>
                        <Switch
                            value={field.value}
                            onValueChange={(val: boolean) => {
                                field.onChange(val);
                                if (!val) {
                                    form.setValue('forwardingChannel', '', { shouldDirty: true });
                                    form.setValue('forwardingEmailAddresses', [], { shouldDirty: true });
                                }
                            }}
                        />
                    </div>
                )}
            />

            {enableAutoforwarding && (
                <div className="flex flex-col gap-3 mt-1">
                    <Controller
                        name="forwardOption"
                        control={form.control}
                        render={({ field }) => (
                            <div className="flex rounded border shadow-control w-fit border-line-strong overflow-hidden">
                                {([ForwardOption.EMAIL, ForwardOption.WORKIST] as const).map((option) => (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => {
                                            field.onChange(option);
                                            if (option === ForwardOption.EMAIL)
                                                form.setValue('forwardingChannel', '', { shouldDirty: true });
                                            if (option === ForwardOption.WORKIST)
                                                form.setValue('forwardingEmailAddresses', [], {
                                                    shouldDirty: true,
                                                });
                                        }}
                                        className={classnames(
                                            'flex-1 py-1.5 px-3 text-sm cursor-pointer font-medium transition-colors border-line-weak',
                                            field.value === option
                                                ? 'bg-surface-brand-weak shadow-none text-content-brand-medium border border-line-teal-50'
                                                : 'bg-surface-white text-content-default hover:bg-surface-medium',
                                            field.value === ForwardOption.EMAIL
                                                ? 'rounded-tl-md rounded-bl-md'
                                                : 'rounded-tr-md rounded-br-md'
                                        )}
                                    >
                                        {t(
                                            option === ForwardOption.EMAIL
                                                ? 'smartInbox.config.events.customCategories.form.forwardToEmail'
                                                : 'smartInbox.config.events.customCategories.form.forwardToWorkist'
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    />

                    {forwardOption === ForwardOption.WORKIST && (
                        <Controller
                            name="forwardingChannel"
                            control={form.control}
                            rules={{
                                validate: (val) =>
                                    !enableAutoforwarding ||
                                    forwardOption !== ForwardOption.WORKIST ||
                                    !!val ||
                                    t('smartInbox.config.events.customCategories.form.validation.channelRequired'),
                            }}
                            render={({ field }) => (
                                <FormField
                                    label={t('smartInbox.config.events.customCategories.form.channel')}
                                    error={formErrors.forwardingChannel}
                                >
                                    <div onWheel={(e) => e.stopPropagation()}>
                                        <SelectField
                                            value={field.value || undefined}
                                            options={channelOptions}
                                            renderOption={(option) => option.label}
                                            placeholder={t(
                                                'smartInbox.config.events.customCategories.form.channelPlaceholder'
                                            )}
                                            onValueChange={field.onChange}
                                        />
                                    </div>
                                </FormField>
                            )}
                        />
                    )}

                    {forwardOption === ForwardOption.EMAIL && (
                        <Controller
                            name="forwardingEmailAddresses"
                            control={form.control}
                            rules={{
                                validate: (val) => {
                                    if (!enableAutoforwarding || forwardOption !== ForwardOption.EMAIL) return true;
                                    if (!val || val.length === 0)
                                        return t(
                                            'smartInbox.config.events.customCategories.form.validation.emailRequired'
                                        );
                                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (!val.every((email) => emailRegex.test(email)))
                                        return t(
                                            'smartInbox.config.events.customCategories.form.validation.invalidEmail'
                                        );
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <FormField error={formErrors.forwardingEmailAddresses}>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-1">
                                            <span className="text-xs text-content-medium">
                                                {t('smartInbox.config.events.customCategories.form.forwardingEmails')}
                                            </span>
                                        </div>
                                        <TagsField
                                            value={field.value || []}
                                            onValueChange={field.onChange}
                                            onBlur={field.onBlur}
                                            placeholder={t(
                                                'smartInbox.config.events.customCategories.form.forwardingEmailsPlaceholder'
                                            )}
                                        />
                                    </div>
                                </FormField>
                            )}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

type Props = {
    form: UseFormReturn<CategoryFormData>;
    channels: Record<string, any>;
    destinationFolders: Array<{ id: string; name: string }>;
    isLoading?: boolean;
    sourceType?: string;
};

const CategoryFormFields = ({ form, channels, destinationFolders, isLoading = false, sourceType }: Props) => (
    <>
        <CategoryDetectionFields form={form} isLoading={isLoading} sourceType={sourceType} />
        <CategoryOutlookActionFields form={form} destinationFolders={destinationFolders} />
        <CategoryAutomationFields form={form} channels={channels} />
    </>
);

export default CategoryFormFields;
